import { useAppService } from '../../providers/app/app.context';
import type { AppService } from '../../providers/app/app.types';
import { SessionType, UserRole } from '../../types';
import { AppRoutes, BusinessPartnerType } from './utils';

/**
 * Who may open a route: allowed Roles (any one of) of the Active Profile, the
 * session type, and Business Partner conditions. Ownership is not an input:
 * a session's Roles come only from its Active Profile (see GLOSSARY.md).
 */
interface GrantRule {
  session?: SessionType;
  roles?: UserRole[];
  notIndividual?: boolean;
  notDivision?: boolean;
}

/** A parent (menu group + landing page) is accessible iff any child is. */
interface ParentRule {
  anyChild: AppRoutes[];
}

export type AccessRule = GrantRule | ParentRule;

export type AccessDecision =
  | { kind: 'allowed' }
  | { kind: 'loading' }
  | { kind: 'denied' }
  | { kind: 'wrongSession'; session: SessionType };

const businessPartnerSession: GrantRule = { session: SessionType.BusinessPartner };
const identitySession: GrantRule = { session: SessionType.NoBusinessPartner };
const userAdministration: GrantRule = {
  session: SessionType.BusinessPartner,
  roles: [UserRole.UserAdministrator],
};
const customerAdministration: GrantRule = {
  session: SessionType.BusinessPartner,
  roles: [UserRole.ServiceCustomerAdministrator],
};
const businessPartnerSettings: GrantRule = {
  session: SessionType.BusinessPartner,
  roles: [UserRole.BusinessPartnerAdministrator, UserRole.ItAdministrator],
};
const itSettings: GrantRule = {
  session: SessionType.BusinessPartner,
  roles: [UserRole.ItAdministrator],
  notIndividual: true,
};

/**
 * The single access rule per route (ADR 0006). Menu entries, route guards,
 * landing cards and parent visibility all derive from it. Routes without an
 * entry are open to every signed-in session.
 */
export const routeAccess: Partial<Record<AppRoutes, AccessRule>> = {
  [AppRoutes.Companies]: identitySession,
  [AppRoutes.CompaniesList]: identitySession,
  [AppRoutes.Invites]: identitySession,
  [AppRoutes.JoinRequests]: identitySession,
  [AppRoutes.UnclaimedOrganizations]: identitySession,
  [AppRoutes.NoBP]: identitySession,
  [AppRoutes.AboutMe]: identitySession,
  [AppRoutes.Divisions]: {
    session: SessionType.BusinessPartner,
    notIndividual: true,
    notDivision: true,
  },
  [AppRoutes.Users]: userAdministration,
  [AppRoutes.Requests]: userAdministration,
  [AppRoutes.Support]: { anyChild: [AppRoutes.Organizations, AppRoutes.TopconId] },
  [AppRoutes.Organizations]: { roles: [UserRole.SupportAllBusinessPartners] },
  [AppRoutes.TopconId]: { roles: [UserRole.SupportAllIdentities] },
  [AppRoutes.Customers]: customerAdministration,
  [AppRoutes.CustomersList]: customerAdministration,
  [AppRoutes.CustomerInvites]: customerAdministration,
  [AppRoutes.CustomerRequests]: customerAdministration,
  [AppRoutes.CustomerSuggestions]: customerAdministration,
  [AppRoutes.Apps]: businessPartnerSession,
  [AppRoutes.Settings]: {
    anyChild: [
      AppRoutes.SettingsGeneralInformation,
      AppRoutes.SettingsAccess,
      AppRoutes.Dealers,
      AppRoutes.SettingsNotifications,
      AppRoutes.SettingsDomainRegistration,
      AppRoutes.SettingsIdentityProviders,
      AppRoutes.SettingsCustomApps,
      AppRoutes.SettingsDeactivateAccount,
      AppRoutes.SettingsLegalDocuments,
    ],
  },
  [AppRoutes.SettingsGeneralInformation]: businessPartnerSettings,
  [AppRoutes.SettingsAccess]: businessPartnerSettings,
  [AppRoutes.Dealers]: {
    session: SessionType.BusinessPartner,
    roles: [UserRole.ServiceProviderAdministrator],
  },
  // No conditions: every Active Profile, in either session type.
  [AppRoutes.SettingsNotifications]: {},
  [AppRoutes.SettingsDomainRegistration]: itSettings,
  [AppRoutes.SettingsIdentityProviders]: itSettings,
  [AppRoutes.SettingsCustomApps]: itSettings,
  [AppRoutes.SettingsDeactivateAccount]: identitySession,
  // No conditions: every Active Profile, in either session type.
  [AppRoutes.SettingsLegalDocuments]: {},
};

const allowed: AccessDecision = { kind: 'allowed' };
const loading: AccessDecision = { kind: 'loading' };
const denied: AccessDecision = { kind: 'denied' };

function decideGrant(rule: GrantRule, app: AppService): AccessDecision {
  const { currentBusinessPartner, loadedBusinessPartner, loadedRoles, userRoles } = app;
  const needsBusinessPartner = !!rule.session || !!rule.notIndividual || !!rule.notDivision;

  if (needsBusinessPartner && !loadedBusinessPartner) return loading;

  const session = currentBusinessPartner
    ? SessionType.BusinessPartner
    : SessionType.NoBusinessPartner;
  if (rule.session && rule.session !== session) {
    return { kind: 'wrongSession', session: rule.session };
  }

  if (rule.roles) {
    if (!loadedRoles) return loading;
    if (!rule.roles.some((role) => userRoles.includes(role))) return denied;
  }

  if (
    rule.notIndividual &&
    currentBusinessPartner?.type === BusinessPartnerType.EndCustomerIndividual
  ) {
    return denied;
  }
  if (rule.notDivision && currentBusinessPartner?.parentBusinessPartnerId) return denied;

  return allowed;
}

export function decideAccess(route: AppRoutes | undefined, app: AppService): AccessDecision {
  const rule = route && routeAccess[route];
  if (!rule) return allowed;
  if (!('anyChild' in rule)) return decideGrant(rule, app);

  const children = rule.anyChild.map((child) => decideAccess(child, app).kind);
  if (children.includes('allowed')) return allowed;
  if (children.includes('loading')) return loading;
  return denied;
}

/** Whether the current session may open `route`. False while Roles or the Business Partner load. */
export function useCanAccess(route: AppRoutes): boolean {
  return decideAccess(route, useAppService()).kind === 'allowed';
}

/**
 * Sidebar `hidden` hook for `route`: hidden unless its access rule allows it.
 * `useIrrelevant` hides an accessible entry that is currently empty or not
 * applicable; it never blocks the route itself.
 */
export function hideUnlessAccessible(route: AppRoutes, useIrrelevant: () => boolean = () => false) {
  return function useHidden() {
    const canAccess = useCanAccess(route);
    const irrelevant = useIrrelevant();
    return !canAccess || irrelevant;
  };
}
